import { SavedSearchCriteria } from '../types/savedSearch';

// Human-readable one-line summary of a saved search's criteria for list rows
// and confirmation copy. Pure so it is unit-testable.

const formatPounds = (value: number): string => `£${value.toLocaleString('en-GB')}`;

function formatRange(
  min: number | undefined,
  max: number | undefined,
  format: (value: number) => string,
  { openUpper, upTo, single }: {
    openUpper: (value: string) => string;
    upTo: (value: string) => string;
    single?: (value: string) => string;
  }
): string | null {
  const hasMin = typeof min === 'number';
  const hasMax = typeof max === 'number';
  if (hasMin && hasMax) {
    if (min === max && single) return single(format(min as number));
    return `${format(min as number)}–${format(max as number)}`;
  }
  if (hasMin) return openUpper(format(min as number));
  if (hasMax) return upTo(format(max as number));
  return null;
}

export function formatSearchCriteria(criteria?: SavedSearchCriteria): string {
  if (!criteria) return 'Any property';
  const parts: string[] = [];

  const location = [criteria.town, criteria.region].filter(Boolean).join(', ');
  if (location) parts.push(location);
  if (criteria.propertyType) parts.push(criteria.propertyType);
  if (criteria.strategy) parts.push(criteria.strategy);

  const price = formatRange(criteria.minPrice, criteria.maxPrice, formatPounds, {
    openUpper: (v) => `${v}+`,
    upTo: (v) => `Up to ${v}`,
  });
  if (price) parts.push(price);

  const beds = formatRange(criteria.minBedRoom, criteria.maxBedRoom, String, {
    openUpper: (v) => `${v}+ beds`,
    upTo: (v) => `Up to ${v} beds`,
    single: (v) => `${v} beds`,
  });
  if (beds) {
    parts.push(beds.includes('beds') ? beds : `${beds} beds`);
  }

  const yieldRange = formatRange(
    criteria.minGrossYield,
    criteria.maxGrossYield,
    String,
    {
      openUpper: (v) => `${v}%+ yield`,
      upTo: (v) => `Up to ${v}% yield`,
    }
  );
  if (yieldRange) {
    parts.push(yieldRange.includes('yield') ? yieldRange : `${yieldRange}% yield`);
  }

  return parts.length > 0 ? parts.join(' · ') : 'Any property';
}
